// --------------------------------------------
// Collection Sort Dropdown - Highend Custom
// --------------------------------------------

export class CollectionSort {
  constructor() {
    this.processedSelects = new WeakSet();
    this.init();
    this.observeNewElements();
  }

  init() {
    const sortSelects = document.querySelectorAll('.facet-filters__sort');
    sortSelects.forEach(select => this.setupCustomDropdown(select));
    
    const mobileSortSelects = document.querySelectorAll('.mobile-facets__sort-select');
    mobileSortSelects.forEach(select => this.setupCustomDropdown(select));
  }

  setupCustomDropdown(select) {
    if (!select || this.processedSelects.has(select)) return;
    this.processedSelects.add(select);

    const selectParent = select.parentElement;
    if (!selectParent) return;

    // Check if already processed
    if (selectParent.querySelector('.sort-dropdown')) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'sort-dropdown';
    wrapper.innerHTML = `
      <button type="button" class="sort-dropdown__button" aria-haspopup="listbox" aria-expanded="false">
        <span class="sort-dropdown__value">${select.options[select.selectedIndex].text}</span>
        <span class="sort-dropdown__arrow">
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
            <path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
      </button>
      <ul class="sort-dropdown__list" role="listbox" tabindex="-1"></ul>
    `;

    const button = wrapper.querySelector('.sort-dropdown__button');
    const list = wrapper.querySelector('.sort-dropdown__list');

    selectParent.insertBefore(wrapper, select);
    wrapper.appendChild(select);
    select.style.position = 'absolute';
    select.style.opacity = '0';
    select.style.pointerEvents = 'none';
    select.style.width = '100%';
    select.style.height = '100%';
    select.style.zIndex = '-1';

    // Remove original SVG caret sibling (we use custom arrow)
    const svgWrapper = selectParent.querySelector('.svg-wrapper');
    if (svgWrapper) svgWrapper.remove();

    const options = Array.from(select.options).map((opt, idx) => ({
      value: opt.value,
      text: opt.text,
      selected: opt.selected
    }));

    list.innerHTML = options.map(opt => `
      <li class="sort-dropdown__option ${opt.selected ? 'is-selected' : ''}" 
          data-value="${opt.value}" 
          role="option"
          aria-selected="${opt.selected}">
        <span class="sort-dropdown__option-text">${opt.text}</span>
        ${opt.selected ? '<span class="sort-dropdown__check"><svg width="12" height="9" viewBox="0 0 12 9" fill="none"><path d="M1 4L4.5 7.5L11 1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></span>' : ''}
      </li>
    `).join('');

    const toggleDropdown = (e) => {
      e.preventDefault();
      const isOpen = wrapper.classList.contains('is-open');
      this.closeAllDropdowns();
      if (!isOpen) {
        wrapper.classList.add('is-open');
        button.setAttribute('aria-expanded', 'true');
        list.querySelector('.is-selected')?.focus();
      }
    };

    const closeDropdown = () => {
      wrapper.classList.remove('is-open');
      button.setAttribute('aria-expanded', 'false');
    };

    button.addEventListener('click', toggleDropdown);

    list.addEventListener('click', (e) => {
      const option = e.target.closest('.sort-dropdown__option');
      if (!option) return;
      
      const value = option.dataset.value;
      select.value = value;
      
      list.querySelectorAll('.is-selected').forEach(el => {
        el.classList.remove('is-selected');
        el.removeAttribute('aria-selected');
        const check = el.querySelector('.sort-dropdown__check');
        if (check) check.remove();
      });
      
      option.classList.add('is-selected');
      option.setAttribute('aria-selected', 'true');
      option.innerHTML += '<span class="sort-dropdown__check"><svg width="12" height="9" viewBox="0 0 12 9" fill="none"><path d="M1 4L4.5 7.5L11 1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';
      
      wrapper.querySelector('.sort-dropdown__value').textContent = option.querySelector('.sort-dropdown__option-text').textContent;
      
      closeDropdown();
      
      select.closest('form')?.submit();
    });

    const handleKeydown = (e) => {
      if (!wrapper.classList.contains('is-open')) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          toggleDropdown(e);
        }
        return;
      }
      
      if (e.key === 'Escape') {
        e.preventDefault();
        closeDropdown();
        button.focus();
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const options = list.querySelectorAll('.sort-dropdown__option');
        const currentIdx = Array.from(options).indexOf(list.querySelector(':focus'));
        let nextIdx;
        if (e.key === 'ArrowDown') {
          nextIdx = currentIdx < options.length - 1 ? currentIdx + 1 : 0;
        } else {
          nextIdx = currentIdx > 0 ? currentIdx - 1 : options.length - 1;
        }
        options[nextIdx].focus();
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const focused = list.querySelector(':focus');
        if (focused) {
          focused.click();
        }
      }
    };

    button.addEventListener('keydown', handleKeydown);
    list.addEventListener('keydown', handleKeydown);

    document.addEventListener('click', (e) => {
      if (!wrapper.contains(e.target)) {
        closeDropdown();
      }
    });
  }

  closeAllDropdowns() {
    document.querySelectorAll('.sort-dropdown.is-open').forEach(dd => {
      dd.classList.remove('is-open');
      dd.querySelector('.sort-dropdown__button')?.setAttribute('aria-expanded', 'false');
    });
  }

  observeNewElements() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType !== 1) return;
          
          // Check if node is a select
          if (node.matches && node.matches('select')) {
            this.setupCustomDropdown(node);
          }
          
          // Check for selects inside the added node
          if (node.querySelectorAll) {
            node.querySelectorAll('select').forEach(select => {
              this.setupCustomDropdown(select);
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

export function initCollectionSort() {
  return new CollectionSort();
}
